import { t } from '@lingui/macro';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { sanitizeSvg } from '../../helpers/sanitizeSvg';

interface GroupLogoProps {
  /** Raw logo value: either inline SVG markup or an image URL / data URI. */
  logo?: string | null;
  /** Rendered width in px. */
  width?: number;
  /** Cap on the rendered height in px; the aspect ratio is preserved. */
  maxHeight?: number;
  hasBorder?: boolean;
  alt?: string;
  className?: string;
  testId?: string;
}

const LogoWrapper = styled.div<{ $width: number; $maxHeight: number; $hasBorder: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $width }) => $width}px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  ${({ $hasBorder, theme }) =>
    $hasBorder && `border: 1px solid ${theme.designTokens.colors.grey30};`}

  svg,
  img {
    width: 100%;
    height: auto;
    max-height: ${({ $maxHeight }) => $maxHeight}px;
    object-fit: contain;
  }
`;

const isInlineSvg = (logo: string) => logo.trim().startsWith('<svg');

const GroupLogo = ({
  logo,
  width = 150,
  maxHeight = 80,
  hasBorder = false,
  alt,
  className,
  testId = 'groupLogo',
}: GroupLogoProps) => {
  // `sanitizeSvg` returns null for unsafe markup and while server side rendering.
  const inlineSvg = useMemo(() => (logo && isInlineSvg(logo) ? sanitizeSvg(logo) : null), [logo]);

  if (!logo) {
    return null;
  }

  const label = alt ?? t`Group logo`;

  if (isInlineSvg(logo)) {
    if (!inlineSvg) {
      return null;
    }

    return (
      <LogoWrapper
        $hasBorder={hasBorder}
        $maxHeight={maxHeight}
        $width={width}
        aria-label={label}
        className={className}
        data-test-id={testId}
        dangerouslySetInnerHTML={{ __html: inlineSvg }}
        role="img"
      />
    );
  }

  return (
    <LogoWrapper
      $hasBorder={hasBorder}
      $maxHeight={maxHeight}
      $width={width}
      className={className}
      data-test-id={testId}
    >
      <img alt={label} src={logo} />
    </LogoWrapper>
  );
};

export { GroupLogo };
export type { GroupLogoProps };
